import { apiClient } from '../utils/api-client';
import type { Gym, GymStats } from '../types/gym';

export interface GymProfileResponse {
  data: {
    id: string;
    type: 'gym';
    attributes: Gym;
  };
}

class GymsService {
  async getGymBySlug(slug: string): Promise<Gym> {
    const response = await apiClient.get(`/api/gyms/${slug}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Gym not found');
      }
      throw new Error('Failed to fetch gym profile');
    }

    const data: GymProfileResponse = await response.json();
    return data.data.attributes;
  }

  async getGymStats(gymId: string): Promise<GymStats> {
    const response = await apiClient.get(`/api/gyms/${gymId}/stats`);

    if (!response.ok) {
      throw new Error('Failed to fetch gym stats');
    }

    const data = await response.json();
    return data.data.attributes;
  }

  async followGym(gymSlug: string): Promise<void> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/follow`, {});

    if (!response.ok) {
      throw new Error('Failed to follow gym');
    }
  }

  async unfollowGym(gymSlug: string): Promise<void> {
    const response = await apiClient.delete(`/api/gyms/${gymSlug}/unfollow`);

    if (!response.ok) {
      throw new Error('Failed to unfollow gym');
    }
  }

  async updateGym(gymSlug: string, data: Partial<Gym>): Promise<Gym> {
    const response = await apiClient.patch(`/api/gyms/${gymSlug}`, {
      data: {
        attributes: data
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.errors?.[0]?.detail || 'Failed to update gym';
      throw new Error(errorMessage);
    }

    const responseData: GymProfileResponse = await response.json();
    return responseData.data.attributes;
  }
}

export const gymsService = new GymsService();
